import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { ArrowRight, Phone, Mail } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function HeroSection() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setFormData({ name: '', email: '', phone: '', company: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <section className="hero-bg min-h-[90vh] flex items-center pt-16 relative" data-testid="hero-section">
      <img
        src="/mlctech-logo.png"
        alt=""
        className="logo-watermark absolute right-10 top-1/2 -translate-y-1/2 h-[500px] w-auto animate-float hidden lg:block"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-2 mb-6 backdrop-blur-sm">
              <div className="w-2 h-2 rounded-full bg-cyan-400 pulse-dot" />
              <span className="text-sm text-cyan-300 font-medium">Affordable Managed IT Services</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
              IT Support That <span className="text-gradient">Doesn't Break</span> Your Budget
            </h1>
            <p className="text-lg text-slate-300 mb-8 max-w-xl leading-relaxed">
              Enterprise-grade cybersecurity, 24/7 monitoring, and responsive help desk support
              — at prices small businesses can actually afford. No surprises, no hidden fees.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/30 btn-hover-lift w-full sm:w-auto" data-testid="hero-get-quote-btn">
                  Get Your Free IT Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-white/10 w-full sm:w-auto" data-testid="hero-view-pricing-btn">
                  View Pricing Plans
                </Button>
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 text-slate-300">
              <a href="tel:6026491314" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
                <Phone className="w-4 h-4" /><span>(602) 649-1314</span>
              </a>
              <a href="mailto:info@mlctech.org" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
                <Mail className="w-4 h-4" /><span>info@mlctech.org</span>
              </a>
            </div>
          </div>

          <div className="animate-fade-in-up stagger-2">
            <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border-0 glow-blue" data-testid="hero-contact-form">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Get a Free IT Assessment
                </h3>
                <p className="text-slate-600 mb-6">Find out how much you could save on IT support.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {['name', 'email', 'phone', 'company'].map((field) => (
                    <Input
                      key={field}
                      type={field === 'email' ? 'email' : 'text'}
                      placeholder={{ name: 'Your Name', email: 'Work Email', phone: 'Phone Number', company: 'Company Name' }[field]}
                      value={formData[field]}
                      onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                      required={field === 'name' || field === 'email'}
                      className="h-12 border-slate-200 focus:border-blue-500 transition-colors"
                      data-testid={`hero-form-${field}`}
                    />
                  ))}
                  <Button type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/20 btn-hover-lift" disabled={isSubmitting} data-testid="hero-form-submit">
                    {isSubmitting ? 'Sending...' : 'Request Free Quote'}
                  </Button>
                </form>
                <p className="text-xs text-slate-500 mt-4 text-center">No obligation. Response within 24 hours.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
